import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ConfigProvider, Space } from 'antd';

import { LiveTag, ManualTag } from '@totem/components/accountPlan/utilities';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import colors from '@totem/styles/colors';

// Brand navy header with white text reads the same in light and dark themes.
const HEADER_TEXT = '#ffffff';
const LIVE_BORDER = '#2563eb';

const headerTheme = {
  token: {
    colorText: HEADER_TEXT,
    colorTextSecondary: 'rgba(255, 255, 255, 0.72)',
  },
};

type Props = {
  title: ReactNode;
  // right side of the header: counts, actions
  extra?: ReactNode;
  // "live" = read-only Portal data (blue rule); "manual" = maintained by the account team (orange rule)
  variant: 'live' | 'manual';
  liveTip?: string;
  onEdit?: () => void;
  portalLink?: string;
  children: ReactNode;
};

const PlanCard = ({
  title,
  extra,
  variant,
  liveTip,
  onEdit,
  portalLink,
  children,
}: Props) => (
  <ConfigProvider
    theme={{
      components: {
        Card: {
          headerBg: colors.branding.primaryDark,
          colorTextHeading: HEADER_TEXT,
          headerFontSize: 16,
          headerFontSizeSM: 16,
          headerHeightSM: 50,
        },
      },
    }}
  >
    <IBCard
      size="small"
      title={
        <Space size={10}>
          {variant === 'live' ? (
            <LiveTag tip={liveTip} />
          ) : (
            <ManualTag onClick={onEdit} />
          )}
          <span style={{ fontWeight: 700 }}>{title}</span>
        </Space>
      }
      extra={
        (extra || portalLink) && (
          <ConfigProvider theme={headerTheme}>
            <Space
              size={12}
              style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.8)' }}
            >
              {portalLink && (
                <Link
                  to={portalLink}
                  style={{ color: HEADER_TEXT, fontWeight: 600 }}
                >
                  Open in portal ↗
                </Link>
              )}
              {extra}
            </Space>
          </ConfigProvider>
        )
      }
      style={{
        height: '100%',
        overflow: 'hidden',
        borderTop: `4px solid ${variant === 'manual' ? colors.branding.accent : LIVE_BORDER}`,
      }}
      styles={{ header: { borderRadius: 0 }, body: { padding: 16 } }}
    >
      {children}
    </IBCard>
  </ConfigProvider>
);

export default PlanCard;
